import { useEffect, useId, useMemo } from 'react'
import { Check, TriangleAlert } from 'lucide-react'
import ColorField from '../../components/ColorField'
import { contrastRatio, normalizeHex } from '../../components/colorUtils'
import {
  DEFAULT_ACTIVE,
  DEFAULT_MAIN,
  applyBranding,
  railColor,
  railMark,
  readBranding,
} from '../../app/branding'
import { settingsStrings as S } from './strings'
import './BrandThemeCard.css'

const T = S.theme

/** AA: 4.5 for text, 3 for a UI graphic. */
const check = (a, b, floor) => {
  const ratio = contrastRatio(a, b)
  if (ratio == null) return null
  return { ratio: Math.round(ratio * 10) / 10, pass: ratio >= floor }
}

/**
 * الهوية البصرية — the client identity pair, as the contents of its sheet.
 *
 * tokens.css is built around `--gov-primary` and `--gov-gold` as "two knobs [that]
 * rebrand the whole app"; this is where they become a setting. The pair is the page
 * form's, saved by the sheet's own Save like every other section; this draws it.
 *
 * While it is being edited the whole product is the preview — the draft is painted onto
 * the live app, behind the sheet — and leaving without saving puts back what is stored.
 *
 * REDESIGNED (2026-10-22): the two colours on the reading side, each a card of its own —
 * what it is, what it paints, the value, and its contrast as a badge that passes or warns
 * — and beside them a fuller miniature of this app in the draft pair: the rail with the
 * current page's wash and gold bead, the header, a primary button, the section tabs and a
 * list card.
 */
export default function BrandThemeCard({ main: rawMain, active: rawActive, onMain, onActive }) {
  const main = normalizeHex(rawMain) || DEFAULT_MAIN
  const active = normalizeHex(rawActive) || DEFAULT_ACTIVE
  const rail = useMemo(() => railColor(main), [main])

  useEffect(() => {
    applyBranding({ main, active })
    return () => applyBranding(readBranding())
  }, [main, active])

  return (
    <div className="brand-theme">
      <div className="brand-theme__controls">
        <ColorCard
          label={T.main}
          value={rawMain}
          placeholder={DEFAULT_MAIN}
          onChange={onMain}
          contrast={{ label: T.mainContrast, result: check(main, '#ffffff', 4.5) }}
        />
        {/* The rail lightens the gold before it draws it, so that is what gets measured —
            see railMark. */}
        <ColorCard
          label={T.active}
          value={rawActive}
          placeholder={DEFAULT_ACTIVE}
          onChange={onActive}
          contrast={{ label: T.activeContrast, result: check(railMark(active), rail, 3) }}
        />
      </div>

      {/* A miniature of THIS app, painted in the draft pair. */}
      <figure className="brand-preview">
        <div
          className="brand-mini"
          style={{ '--mini-main': main, '--mini-active': active, '--mini-rail': rail }}
          aria-hidden="true"
        >
          <span className="brand-mini__rail">
            <i className="brand-mini__logo" />
            <i className="brand-mini__link" />
            <i className="brand-mini__link brand-mini__link--on" />
            <i className="brand-mini__link" />
            <i className="brand-mini__link brand-mini__link--short" />
          </span>
          <span className="brand-mini__page">
            <span className="brand-mini__topbar">
              <i className="brand-mini__search" />
              <i className="brand-mini__avatar" />
            </span>
            <span className="brand-mini__head">
              <i className="brand-mini__title" />
              <i className="brand-mini__btn" />
            </span>
            <span className="brand-mini__tabs">
              <i className="brand-mini__tab brand-mini__tab--on" />
              <i className="brand-mini__tab" />
              <i className="brand-mini__tab" />
            </span>
            <span className="brand-mini__card">
              {[0, 1, 2].map((row) => (
                <span key={row} className="brand-mini__line">
                  <i className="brand-mini__text" />
                  <i className="brand-mini__chip" />
                  <i className="brand-mini__action" />
                </span>
              ))}
            </span>
          </span>
        </div>
      </figure>
    </div>
  )
}

/** One colour of the pair: its name, its value, and its contrast as a small badge. */
function ColorCard({ label, value, placeholder, onChange, contrast }) {
  const id = useId()
  return (
    <div className="brand-color">
      <div className="brand-color__head">
        <label className="brand-color__name" htmlFor={id}>
          <bdi>{label}</bdi>
        </label>
        <Readout label={contrast.label} result={contrast.result} />
      </div>
      <ColorField id={id} value={value} placeholder={placeholder} onChange={onChange} />
    </div>
  )
}

/** One contrast figure: a mark and the ratio. What it measures is its tooltip and its
    accessible name — said once on demand, not printed under every colour. */
function Readout({ label, result }) {
  if (!result) return null
  const verdict = result.pass ? T.pass : T.weak
  return (
    <p
      className={`brand-check${result.pass ? ' is-pass' : ' is-weak'}`}
      title={`${label} — ${verdict}`}
      aria-label={`${label}: ${result.ratio}:1, ${verdict}`}
    >
      {result.pass ? (
        <Check size={13} strokeWidth={2.5} aria-hidden="true" />
      ) : (
        <TriangleAlert size={13} strokeWidth={2} aria-hidden="true" />
      )}
      <span className="brand-check__ratio" dir="ltr" aria-hidden="true">
        {result.ratio}:1
      </span>
    </p>
  )
}
